import type { MouseEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import CheckAnimatedIcon from '@/components/ui/CheckAnimatedIcon';
import TrashAnimatedIcon from '@/components/ui/TrashAnimatedIcon';
import UndoAnimatedIcon from '@/components/ui/UndoAnimatedIcon';

/**
 * ConfirmActionModal - Modal padronizado para confirmação de ações
 *
 * @example
 * // Variante primary (edição em lote)
 * <ConfirmActionModal
 *   isOpen={showBulkEditConfirm}
 *   onClose={() => setShowBulkEditConfirm(false)}
 *   onConfirm={handleBulkEdit}
 *   title="Editar em lote"
 *   subtitle="5 itens selecionados"
 *   variant="primary"
 * >
 *   <p>Deseja editar os itens selecionados?</p>
 * </ConfirmActionModal>
 *
 * @example
 * // Variante danger (exclusão em lote)
 * <ConfirmActionModal
 *   isOpen={showBulkDeleteConfirm}
 *   onClose={() => setShowBulkDeleteConfirm(false)}
 *   onConfirm={handleBulkDelete}
 *   title="Excluir selecionados"
 *   subtitle="Esta ação não pode ser desfeita"
 *   variant="danger"
 *   confirmText="Excluir"
 * >
 *   <p>Deseja realmente excluir os 5 itens selecionados?</p>
 * </ConfirmActionModal>
 */
export interface ConfirmActionModalProps {
  /** Controla visibilidade do modal */
  isOpen: boolean;
  /** Callback ao fechar/cancelar */
  onClose: () => void;
  /** Callback ao confirmar ação */
  onConfirm: () => void;
  /** Título do modal (ex: "Editar em lote", "Excluir selecionados") */
  title?: string;
  /** Subtítulo opcional */
  subtitle?: string;
  /** Conteúdo customizado para mostrar detalhes */
  children?: ReactNode;
  /** Texto do botão de confirmação (default: "Confirmar") */
  confirmText?: string;
  /** Texto do botão de cancelar (default: "Cancelar") */
  cancelText?: string;
  /** Estado de loading durante ação */
  loading?: boolean;
  /** Texto durante loading (default: "Processando...") */
  loadingText?: string;
  /** Variante do modal: 'primary' (ações normais) ou 'danger' (exclusão) */
  variant?: 'primary' | 'danger';
}

export default function ConfirmActionModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirmar ação',
  subtitle,
  children,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  loading = false,
  loadingText = 'Processando...',
  variant = 'primary'
}: ConfirmActionModalProps) {
  if (!isOpen) return null;

  const isDanger = variant === 'danger';
  const iconColor = isDanger ? 'var(--danger)' : 'var(--color-primary)';
  const buttonClass = isDanger ? 'btn-add gf-trash-animated' : 'btn-add checkmark-animated';
  const contentBg = isDanger ? '#fef2f2' : '#f0f9ff';

  const handleConfirm = () => {
    if (!loading) {
      onConfirm();
    }
  };

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10002
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '450px',
          width: '90%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <span style={{ display: 'inline-flex', flexShrink: 0, color: iconColor }}>
            {isDanger
              ? <AlertTriangle size={20} />
              : <CheckAnimatedIcon size={20} />
            }
          </span>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              {title}
            </h3>
            {subtitle && (
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted)' }}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Content */}
        {children && (
          <div style={{
            padding: '16px',
            background: contentBg,
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            lineHeight: '1.5',
            color: '#374151'
          }}>
            {children}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            className="btn-add undo-animated"
            onClick={onClose}
            disabled={loading}
          >
            <UndoAnimatedIcon size={15} /> <span className="btn-text">{cancelText}</span>
          </button>
          <button
            className={buttonClass}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <div style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid rgba(21, 70, 128, 0.3)',
                  borderTop: '2px solid #154680',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span className="btn-text">{loadingText}</span>
              </>
            ) : (
              <>
                {isDanger ? <TrashAnimatedIcon size={15} /> : <CheckAnimatedIcon size={15} />}
                <span className="btn-text">{confirmText}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Keyframe para spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>,
    document.body
  );
}
